import React, { useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  AckTable,
  AckTableRowFormatter,
  AckTableRowProps,
  convertToDisplayValue,
  formatCurrency,
  formatDecimal,
  ModelSpec,
  useFetch,
} from "ackbox-weblib-react";
import { Empty, formatDecoratedCurrency, RESOURCES_API_PATH, RESOURCES_UI_PATH, useEffectOnce } from "./Constants";
import { Progress, useProgress } from "./Progress";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const SimulationsBreakdown = () => {
  const [rows, setRows] = useState([] as AckTableRowProps[]);
  const [overviewData, setOverviewData] = useState([] as any[]);
  const [flowData, setFlowData] = useState([] as any[]);
  const ackFetch = useFetch(RESOURCES_API_PATH);

  const loadSimulationRows = async () => {
    const values = await ackFetch.get("simulations/");
    const spec: ModelSpec[] = await ackFetch.get("simulations/spec/");
    const filteredSpec = spec.filter(
      s => s.name === "isActive"
        || s.name === "createdAt"
        || s.name === "end"
        || s.name === "interestPercent"
        || s.name === "contributionAmount"
        || s.name === "description");
    const rows: AckTableRowProps[] = values.map((value: any) => ({ spec: filteredSpec, payload: value }));
    setRows(rows);
  };

  const loadSimulationOverviewData = async () => {
    const values = await ackFetch.get("simulations/overview/");
    setOverviewData(values);
  };

  const loadSimulationFlowData = async () => {
    const values = await ackFetch.get("simulations/flow/");
    setFlowData(values);
  };

  const { errors, isLoading } = useEffectOnce(async () => {
    await loadSimulationRows();
    await loadSimulationOverviewData();
    await loadSimulationFlowData();
  });

  const Chart = () => {
    const formatter = (value: any) => formatCurrency(value);
    return (
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <AreaChart data={overviewData} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
            <CartesianGrid strokeDasharray="2 2"/>
            <XAxis dataKey="time" angle={-45} height={60} interval={0} tickMargin={20} tick={{ fontSize: 10 }}/>
            <YAxis tickCount={6} interval={0} tick={{ fontSize: 12 }} tickFormatter={formatter}/>
            <Tooltip formatter={formatter}/>
            <Legend layout="horizontal"/>
            <Area dataKey="baseline" type="monotone" stroke="#1abc9c" fill="#1abc9c"/>
            <Area dataKey="simulation" type="monotone" stroke="#f1c40f" fill="#f1c40f"/>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const SimulationOverview = () => {
    return (
      <>
        <h1 className="title is-4">Overview</h1>
        {!isLoading && overviewData.length === 0 && <Empty/>}
        {!isLoading && overviewData.length > 0 && <Chart/>}
      </>
    );
  };

  const SimulationsActiveTable = () => {
    const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
      if (spec.name === "interestPercent") {
        return `${formatDecimal(payload[spec.name])}% / ${payload.interestPeriod.toLocaleLowerCase()}`;
      }
      if (spec.name === "contributionAmount") {
        const sign = payload.type === "EXPENSE" ? -1 : 1;
        return `${formatCurrency(sign * payload[spec.name])} / ${payload.contributionPeriod.toLocaleLowerCase()}`;
      }
      return convertToDisplayValue(spec, payload);
    };
    return (
      <>
        <h1 className="title is-4">Active Simulations</h1>
        <div className="buttons are-small">
          <a className="button is-light is-small" href={`${RESOURCES_UI_PATH}/simulations`}>List</a>
          <a className="button is-primary is-small" href={`${RESOURCES_UI_PATH}/simulations/create`}>Create</a>
        </div>
        {!isLoading && rows.length === 0 && <Empty/>}
        {!isLoading && rows.length > 0 && <AckTable rows={rows} formatters={formatters}/>}
      </>
    );
  };

  const SimulationsFlowTable = () => {
    const spec: ModelSpec[] = [
      { key: "id", label: "Year", name: "id", type: "string" },
      { key: "incomes", label: "Inflow", name: "incomes", type: "decimal" },
      { key: "expenses", label: "Outflow", name: "expenses", type: "decimal" },
      { key: "flowTotal", label: "Total", name: "flowTotal", type: "decimal" },
      { key: "total", label: "Running Total", name: "total", type: "decimal" },
    ];
    const rows: AckTableRowProps[] = flowData.map((value: any) => ({ spec: spec, payload: value }));
    const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
      if (spec.name === "incomes" || spec.name === "expenses" || spec.name === "total") {
        return formatDecoratedCurrency(payload[spec.name]);
      }
      if (spec.name === "flowTotal") {
        return formatDecoratedCurrency(payload["incomes"] + payload["expenses"]);
      }
      return convertToDisplayValue(spec, payload);
    };
    return (
      <>
        <h1 className="title is-4">Flow Simulations</h1>
        {!isLoading && rows.length === 0 && <Empty/>}
        {!isLoading && rows.length > 0 && <AckTable rows={rows} formatters={formatters} hideIndexCol={true}/>}
      </>
    );
  };

  return (
    <>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      <SimulationOverview/>
      <SimulationsActiveTable/>
      <SimulationsFlowTable/>
    </>
  );
};

export const Simulations = () => {
  const progress = useProgress();
  return (
    <div className="section">
      <h1 className="title is-spaced">Simulations</h1>
      {!progress.isComplete && <Progress {...progress}/>}
      {progress.isComplete && <SimulationsBreakdown/>}
    </div>
  );
};
